import React, { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../assets/icons/index';
import { PaginationController } from '../../../components/pagination-controller/index';
import {
  Select,
  SelectButton,
  SelectOption,
  SelectOptions,
} from '../../../components/select/index';
import {
  CompanyOrder,
  NumberOfFetchedItems,
  SelectNumberOfFetchedItems,
  selectVariant,
} from '../../../types/company.types';
import { OfferSortBy, SubscriptionOffer } from '../../../types/offer.types';
import { TableSortingCell } from '../company-tab/table-sorting-cell';
import { SubscriptionTableItem } from './subscription-table-item';

export type SubscriptionTabProps = {
  currentPage: number;
  handleDelete: (offer: SubscriptionOffer) => void;
  handleEdit: (offer: SubscriptionOffer) => void;
  handlePageChange: (page: number) => void;
  handleSelectNumberOfFetchedItems: (select: SelectNumberOfFetchedItems) => void;
  handleView: (offer: SubscriptionOffer) => void;
  isMobile: boolean;
  numberOfFetchedItems: NumberOfFetchedItems;
  offers: SubscriptionOffer[];
  onSortClick: (field: OfferSortBy) => void;
  sortField: null | OfferSortBy;
  sortMode: CompanyOrder | null;
  totalPages: number;
};

const SORTABLE_HEADERS: { sortBy: null | OfferSortBy; title: string }[] = [
  { title: 'Name', sortBy: OfferSortBy.COMPANY_NAME },
  { title: 'Company Domain', sortBy: null },
  { title: 'Stake Limit', sortBy: OfferSortBy.STAKEHOLDERS_LIMIT },
  { title: 'Recipients', sortBy: OfferSortBy.RECIPIENTS },
  { title: 'Plan', sortBy: OfferSortBy.PLAN_TYPE },
];

export const SubscriptionTab: FC<SubscriptionTabProps> = ({
  offers,
  currentPage,
  totalPages,
  handlePageChange,
  handleView,
  handleEdit,
  handleDelete,
  isMobile,
  handleSelectNumberOfFetchedItems,
  numberOfFetchedItems,
  onSortClick,
  sortField,
  sortMode,
}) => {
  return (
    <>
      <div className="w-full shrink-0 overflow-x-auto *:text-nowrap">
        <table className="h-fit w-full divide-y-[1px] divide-[#F2F2F2] border-b-[1px] border-[#F2F2F2]">
          <thead>
            <tr className="h-11 bg-gray-50">
              {SORTABLE_HEADERS.map(({ title, sortBy }) => (
                <td
                  className="cursor-pointer pl-4 text-label-md font-[450] text-[#172335]"
                  key={title}
                >
                  {sortBy ? (
                    <TableSortingCell
                      onSortClick={onSortClick}
                      sortByCell={sortBy}
                      sortField={sortField}
                      sortMode={sortMode}
                      title={title}
                    />
                  ) : (
                    title
                  )}
                </td>
              ))}
              <td className="w-12" />
            </tr>
          </thead>
          <tbody className="divide-y-[1px] divide-[#F2F2F2] bg-gray-600">
            {offers.map((offer) => (
              <SubscriptionTableItem
                handleDelete={handleDelete}
                handleEdit={handleEdit}
                handleView={handleView}
                key={offer.id}
                offer={offer}
              />
            ))}
          </tbody>
        </table>
        {offers.length === 0 && (
          <div className="flex w-full items-center justify-center py-10 text-sm text-gray-400">
            No pending subscriptions
          </div>
        )}
      </div>
      <div className="flex w-full items-center justify-between px-6 pt-4">
        {!isMobile && (
          <Select onChange={handleSelectNumberOfFetchedItems} value={numberOfFetchedItems}>
            {({ open }) => (
              <div className="relative rounded border-gray-200 py-4">
                <SelectButton className="flex items-center gap-4 bg-gray-25 py-[10px] pl-[12px] pr-[6px]">
                  <span className="text-m font-[450] text-gray-700">{numberOfFetchedItems}</span>
                  <ChevronDownIcon
                    className={twMerge(
                      'h-[13px] w-[13px] transition-transform duration-200',
                      open && 'rotate-180',
                    )}
                  />
                </SelectButton>
                <SelectOptions className="absolute left-0 right-0 top-[-6px] mt-1 max-h-60 w-[58px] -translate-y-full transform overflow-auto rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {selectVariant.map((select) => (
                    <SelectOption key={select} value={select}>
                      <span className="block text-m font-normal text-gray-700">{select}</span>
                    </SelectOption>
                  ))}
                </SelectOptions>
              </div>
            )}
          </Select>
        )}
        <PaginationController
          className="ml-auto shrink-0"
          currentPage={currentPage}
          onClick={handlePageChange}
          totalPages={totalPages || 0}
        />
      </div>
    </>
  );
};
